import type { ReactNode } from "react";
import { type Control, Controller, type FieldValues } from "react-hook-form";

import FileDownloadIcon from "@mui/icons-material/FileDownload";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import useTheme from "@mui/material/styles/useTheme";

import { UPLOAD_FILE_ACCEPTED_TYPE_MAP } from "../utils";
import DropBox from "./drop-box.component";

type UploadFileFromComputerProps = {
  name: string;
  control: Control<FieldValues, any>;
  label?: ReactNode;
  sizeLimitLabel?: ReactNode;
  acceptedType?: keyof typeof UPLOAD_FILE_ACCEPTED_TYPE_MAP;
  onFileDrop: (e: React.SyntheticEvent<EventTarget>) => void;
  wrapperRef: React.RefObject<HTMLDivElement>;
};

export default function UploadFileFromComputer({
  name,
  control,
  acceptedType,
  label,
  sizeLimitLabel,
  onFileDrop,
  wrapperRef
}: UploadFileFromComputerProps) {
  const theme = useTheme();

  // const acceptedTypeTitle = acceptedType
  //   ? UPLOAD_FILE_ACCEPTED_TYPE_MAP[acceptedType]
  //   : "";

  const onDragEnter = () => wrapperRef.current?.classList.add("dragover");
  const onDragLeave = () => wrapperRef.current?.classList.remove("dragover");

  return (
    <DropBox width="100%">
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{
          position: "relative",
          minWidth: "300px",
          width: "100%",
          height: "6rem",
          border: `1px dashed ${theme.palette.secondary.main}`,
          borderRadius: "12px",
          boxSizing: "border-box"
        }}
        ref={wrapperRef}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDrop={onDragLeave}
      >
        <Stack justifyContent="center" sx={{ p: 1, textAlign: "center" }}>
          <div>
            <FileDownloadIcon />
          </div>
          <Stack>
            <Typography variant="body2" component="span">
              {label}
            </Typography>
            <Typography variant="body1" component="span" color={grey[600]}>
              {sizeLimitLabel}
            </Typography>
          </Stack>
        </Stack>
        <Controller
          name={name}
          defaultValue=""
          control={control}
          render={({ field: { name, onBlur, ref } }) => (
            <input
              type="file"
              multiple
              name={name}
              onBlur={onBlur}
              ref={ref}
              onChange={onFileDrop}
              accept={acceptedType}
              style={{
                opacity: 0,
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                cursor: "pointer"
              }}
            />
          )}
        />
      </Box>
    </DropBox>
  );
}
